import type { components } from "./schema";

type S = components["schemas"];
export type StoryCard = S["StoryCard"];
export type StoryPage = S["StoryPage"];
export type StoryList = S["StoryList"];
export type TermRef = S["TermRef"];
export type Span = S["Span"];
export type TermSheet = S["TermSheet"];
export type TermState = S["TermState"];
export type MyTerm = S["MyTerm"];
export type Me = S["Me"];
export type PostOut = S["PostOut"];
export type PostType = S["PostIn"]["type"];
export type Alert = S["Alert"];
export type SearchResults = S["SearchResults"];

// The app and its API live under the vite base: /learn/api/v1 on the same origin.
export const API_BASE = `${import.meta.env.BASE_URL}api/v1`;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "same-origin",
    // Required by the API on every state-changing request (CSRF protection).
    headers: { "X-Requested-With": "learn", ...(body ? { "Content-Type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const e = data?.error;
    throw new ApiError(res.status, e?.code ?? "error", e?.message ?? `Request failed (${res.status})`);
  }
  return data as T;
}

const q = (params: Record<string, string | undefined>) => {
  const s = new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]);
  return s.size ? `?${s}` : "";
};

export const api = {
  providers: () => request<{ providers: string[] }>("GET", "/auth/providers"),
  devLogin: (email: string, name: string) => request<void>("POST", "/auth/dev-login", { email, name }),
  logout: () => request<void>("POST", "/auth/logout"),
  me: () => request<Me | null>("GET", "/auth/session"),
  patchMe: (patch: Partial<Pick<Me, "level" | "digest_enabled" | "name" | "timezone" | "quiet_hours_enabled" | "quiet_start" | "quiet_end" | "theme">>) =>
    request<Me>("PATCH", "/me", patch),
  deleteMe: () => request<void>("DELETE", "/me"),

  topics: () => request<{ topics: string[] }>("GET", "/topics"),
  languages: () => request<{ languages: string[] }>("GET", "/languages"),
  stories: (topic?: string, language?: string, cursor?: string | null) =>
    request<StoryList>("GET", `/stories${q({ topic, language, cursor: cursor ?? undefined })}`),
  search: (text: string) => request<SearchResults>("GET", `/search${q({ q: text })}`),
  story: (id: string) => request<StoryPage>("GET", `/stories/${id}`),

  term: (id: string, storyId?: string) =>
    request<TermSheet>("GET", `/terms/${id}${q({ story_id: storyId })}`),
  myTerms: (state: "learning" | "understood") => request<MyTerm[]>("GET", `/me/terms?state=${state}`),
  setTerm: (id: string, patch: { understood?: boolean; saved?: boolean }) =>
    request<TermState>("PUT", `/me/terms/${id}`, patch),
  follows: () => request<{ terms: TermRef[] }>("GET", "/me/follows"),
  follow: (id: string) => request<TermState>("PUT", `/me/follows/terms/${id}`),
  unfollow: (id: string) => request<void>("DELETE", `/me/follows/terms/${id}`),

  alerts: () => request<Alert[]>("GET", "/me/alerts"),
  privacy: () => request<S["PolicyFacts"]>("GET", "/privacy"),
  pushKey: () => request<{ public_key: string | null }>("GET", "/push/key"),
  savePush: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    request<{ push_enabled: boolean }>("POST", "/me/push-subscriptions", sub),
  deletePush: (endpoint: string) => request<void>("DELETE", "/me/push-subscriptions", { endpoint }),

  posts: (storyId: string) => request<{ items: PostOut[] }>("GET", `/stories/${storyId}/posts`),
  post: (storyId: string, body: { type: PostType; text: string; parent_id?: string }) =>
    request<PostOut>("POST", `/stories/${storyId}/posts`, body),
  accept: (postId: string) => request<PostOut>("POST", `/posts/${postId}/accept`),
  deletePost: (postId: string) => request<void>("DELETE", `/posts/${postId}`),
};
